import { tracked } from '@glimmer/tracking';
import cloneDeep from 'lodash.clonedeep';

import Utils from '../utils/utils';
import Resistor from './general/resistor';

export default class ResistorHandler {
  cdpParameters;
  @tracked availableResistors = [];

  constructor(cdpParameters) {
    this.cdpParameters = cdpParameters;
  }

  handleDacTypeChange(selectedType, selectedDac) {
    // Create the resistors for the newly selected dac type
    const resistors = (selectedType.resistors || []).map((item) => {
      let resistor = cloneDeep(item);
      Utils.generateNamingMetadata(resistor);
      resistor.cdpParameters = this.cdpParameters;
      return new Resistor(resistor);
    });

    this.availableResistors = resistors;

    this.handleSelectedDacChange(selectedDac);
  }

  handleSelectedDacChange(selectedDac) {
    // Reset any selected dac dependent resistor properties
    this.availableResistors.forEach((resistor) => {
      if (resistor.id === 'external rset') resistor.loadRsetFromPst(selectedDac);
      if (resistor.hasInternalCapabilities) {
        resistor.resetHasInternalParams(selectedDac);
        resistor.resetInternalResistors(selectedDac);
      }
    });
  }

  handleTopologyInputChange(topologyInputs) {
    this.resetCurrentResistors(topologyInputs);
  }

  resetCurrentResistors(topologyInputs) {
    this.availableResistors.forEach((resistor) =>
      resistor.resetProperty('isCurrent', this.isResistorInTopology(resistor, topologyInputs)),
    );
    this.resetResistorValues();
  }

  isResistorInTopology(resistor, topologyInputs) {
    return !!topologyInputs.find((input) => this.isResistorInSelectedTopologyOption(resistor, input.selectedOption));
  }

  isResistorInSelectedTopologyOption(resistor, selectedOption) {
    if (!(selectedOption && selectedOption.resistors)) return false;
    return selectedOption.resistors.includes(resistor.id);
  }

  /**
   * These defaults will reset every time the selected topology changes or the selected dac type
   * changes.  If you don't want that property reset, don't configure it in the reset properties
   */
  resetResistorValues() {
    // Reset defaults for resistors that have internal params and are current in the app (apply to the selected topology)
    const currentResistorIds = this.currentResistors.map((resistor) => resistor.id);
    this.availableResistors
      .filter((resistor) => resistor.hasInternalParamsKey && currentResistorIds.includes(resistor.id))
      .forEach((resistor) => {
        resistor.resetProperty('isDisabled', resistor.hasInternalResistors);

        Object.keys(resistor.reset || {}).forEach((key) => {
          if (resistor.hasInternalResistors) resistor.resetProperty(key, null);
          else if (!resistor[key]) resistor.resetProperty(key, resistor.reset[key]);
        });
      });

    // Reset defaults for resistors that have internal params and don't apply to the selected topology
    this.availableResistors
      .filter((resistor) => resistor.hasInternalParamsKey && !currentResistorIds.includes(resistor.id))
      .forEach((resistor) => {
        resistor.resetProperty('isDisabled', true);
        Object.keys(resistor.reset || {}).forEach((key) => resistor.resetProperty(key, null));
      });

    // Reset non-external resistors
    this.availableResistors
      .filter((resistor) => !resistor.hasInternalParamsKey)
      .forEach((resistor) => {
        if (resistor.isCurrent) {
          Object.keys(resistor.reset || {}).forEach((key) => {
            if (!resistor[key]) resistor.resetProperty(key, resistor.reset[key]);
          });
        } else Object.keys(resistor.reset || {}).forEach((key) => resistor.resetProperty(key, null));
      });
  }

  get currentResistors() {
    if (!this.availableResistors) return [];
    return this.availableResistors.filter((resistor) => resistor.isCurrent);
  }

  get currentInvertingResistor() {
    return this.currentResistors.find((resistor) => resistor.isInverting);
  }
}
